/**
 * Grava e remove instruções da simulação na API, sem substituir o cenário
 * local por uma reconsulta que ainda não trouxe o item.
 */

import { isAPIError, readServiceError } from "@/core/services";
import {
  adicionarInstrucao,
  buscarSimulacaoAtiva,
  InstrucaoGravadaEmParte,
  removerInstrucao,
} from "../simulacao.gateway";
import type { ItemSimulacaoApi, SimulacaoAtivaApi } from "../simulacao.gateway";
import { alteracaoParaInstrucao, instrucaoParaAlteracao } from "./simulacao.instrucao";
import { chaveAlteracao } from "./simulacao.model";
import type { AlteracaoSimulada } from "./simulacao.model";
import {
  limparSimulacaoSessao,
  resolverIdSimulacaoParaInstrucao,
  sincronizarSimulacaoSessao,
} from "./simulacao-sessao";

export function idDaInstrucao(itens: ItemSimulacaoApi[], chave: string): string | undefined {
  for (const item of itens) {
    const alteracao = instrucaoParaAlteracao(item);
    if (alteracao && chaveAlteracao(alteracao) === chave) return item.id;
  }
}

function alteracaoVoltouNaAtiva(ativa: SimulacaoAtivaApi | null, chave: string): boolean {
  return Boolean(ativa && idDaInstrucao(ativa.itens, chave));
}

function isSimulacaoNaoAtiva(error: unknown): boolean {
  return isAPIError(error) && readServiceError(error).code === "SIMULACAO_NAO_ATIVA";
}

/** A tela passa a mostrar o que o core gravou; sem a reconsulta, segue com o cenário que tinha. */
async function hidratarComOGravado(hidratar: (ativa: SimulacaoAtivaApi) => void): Promise<void> {
  try {
    const ativa = await buscarSimulacaoAtiva();
    sincronizarSimulacaoSessao(ativa);
    if (ativa) hidratar(ativa);
  } catch {
    // O erro que importa à tela é o da gravação, propagado por quem chamou.
  }
}

async function gravarInstrucao(
  simulacaoId: string,
  alteracao: AlteracaoSimulada,
  hidratar: (ativa: SimulacaoAtivaApi) => void,
  incluir: (alteracao: AlteracaoSimulada) => void,
  registrarInstrucao: (chave: string, itemId: string) => void,
): Promise<void> {
  let item: ItemSimulacaoApi | null;
  try {
    item = await adicionarInstrucao(simulacaoId, alteracaoParaInstrucao(alteracao));
  } catch (error) {
    if (!(error instanceof InstrucaoGravadaEmParte)) throw error;
    await hidratarComOGravado(hidratar);
    throw error.cause;
  }
  const chave = chaveAlteracao(alteracao);
  const ativa = await buscarSimulacaoAtiva();
  sincronizarSimulacaoSessao(ativa);
  if (alteracaoVoltouNaAtiva(ativa, chave) && ativa) {
    hidratar(ativa);
    return;
  }

  incluir(alteracao);
  if (item?.id) registrarInstrucao(chave, item.id);
}

export async function persistirAlteracaoNaSimulacao(params: {
  alteracao: AlteracaoSimulada;
  incluir: (alteracao: AlteracaoSimulada) => void;
  hidratar: (ativa: SimulacaoAtivaApi) => void;
  registrarInstrucao: (chave: string, itemId: string) => void;
}): Promise<void> {
  const { alteracao, incluir, hidratar, registrarInstrucao } = params;
  const simulacaoId = await resolverIdSimulacaoParaInstrucao();
  if (!simulacaoId) {
    incluir(alteracao);
    return;
  }

  try {
    await gravarInstrucao(simulacaoId, alteracao, hidratar, incluir, registrarInstrucao);
  } catch (error) {
    if (!isSimulacaoNaoAtiva(error)) throw error;
    limparSimulacaoSessao();
    const idAtualizado = await resolverIdSimulacaoParaInstrucao();
    const ativaRecuperada = await buscarSimulacaoAtiva();
    if (ativaRecuperada) hidratar(ativaRecuperada);
    if (!idAtualizado) throw error;
    await gravarInstrucao(idAtualizado, alteracao, hidratar, incluir, registrarInstrucao);
  }
}

export async function removerAlteracaoDaSimulacao(params: {
  chave: string;
  instrucaoId: string | undefined;
  remover: (chave: string) => void;
  hidratar: (ativa: SimulacaoAtivaApi) => void;
}): Promise<"ok" | "falhou"> {
  const { chave, remover, hidratar } = params;
  const simulacaoId = await resolverIdSimulacaoParaInstrucao();
  if (!simulacaoId) {
    remover(chave);
    return "ok";
  }

  let instrucaoId = params.instrucaoId;
  if (!instrucaoId) {
    try {
      const ativa = await buscarSimulacaoAtiva();
      sincronizarSimulacaoSessao(ativa);
      instrucaoId = ativa ? idDaInstrucao(ativa.itens, chave) : undefined;
    } catch {
      return "falhou";
    }
    if (!instrucaoId) {
      remover(chave);
      return "ok";
    }
  }

  try {
    await removerInstrucao(simulacaoId, instrucaoId);
    const ativa = await buscarSimulacaoAtiva();
    sincronizarSimulacaoSessao(ativa);
    if (ativa) hidratar(ativa);
    else remover(chave);
    return "ok";
  } catch (error) {
    if (!isSimulacaoNaoAtiva(error)) return "falhou";
    limparSimulacaoSessao();
    const idAtualizado = await resolverIdSimulacaoParaInstrucao();
    if (!idAtualizado) return "falhou";
    try {
      await removerInstrucao(idAtualizado, instrucaoId);
      const ativa = await buscarSimulacaoAtiva();
      sincronizarSimulacaoSessao(ativa);
      if (ativa) hidratar(ativa);
      else remover(chave);
      return "ok";
    } catch {
      return "falhou";
    }
  }
}
